import { createContext, ReactNode, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';

import axios from 'axios';
import { enqueueSnackbar } from 'notistack';

import { ApplicationErrorModal } from 'components/modal/ApplicationErrorModal';
import { useAuthContext } from 'context/AuthContext';

export const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/proxy/v2';

const api = axios.create({
    baseURL: BASE_URL,
    timeout: 120000,
    maxBodyLength: Infinity,
    maxContentLength: Infinity,

    headers: {
        'content-type': 'application/json',
        'accept': '*/*',
    },
});

type ApiHeaders = Record<string, string | number | null>;

type ApiContext = {
    api: typeof api;
    setApiHeader: (key: keyof ApiHeaders, value: ApiHeaders[keyof ApiHeaders]) => void;
};

const ApiContext = createContext<ApiContext>({} as ApiContext);

export function ApiProvider({ children }: { children: ReactNode }) {
    const { token, stopTokenRenewal } = useAuthContext();
    const headersRef = useRef<ApiHeaders>({});
    const [showAuthenticationErrorModal, setShowAuthenticationErrorModal] = useState(false);

    // mantém a referência do token atualizada
    // sem precisar adicionar no useEffect e reconstruir os interceptors
    headersRef.current['Authorization'] = `Bearer ${token}`;

    /**
     * permite que outros fluxos injetem headers dinâmicos.
     */
    const setApiHeader = (key: keyof ApiHeaders, value: ApiHeaders[keyof ApiHeaders]) => (headersRef.current[key] = value);

    /**
     * - cria um interceptor de request
     * - injeta os valores do header na request
     */
    const createRequestInterceptor = () =>
        api.interceptors.request.use((config) => {
            Object.keys(headersRef.current).forEach((key) => config.headers.set(key, headersRef.current[key]));

            return config;
        });

    /**
     * - cria um interceptor de response
     * - trata 401 e timeout
     */
    const createResponseInterceptor = () =>
        api.interceptors.response.use(
            (response) => response,
            (error) => {
                const { response, code } = error;

                // token inválido ou expirado exibe modal de erro
                if (response?.status === 401) {
                    stopTokenRenewal();

                    setShowAuthenticationErrorModal(true);
                }

                // timeout do axios
                if (code === 'ECONNABORTED') {
                    enqueueSnackbar('Tempo limite de consulta esgotado', {
                        variant: 'error',
                        autoHideDuration: 8000,
                    });
                }

                return Promise.reject(response ?? error);
            },
        );

    useLayoutEffect(() => {
        const requestInterceptor = createRequestInterceptor();
        const responseInterceptor = createResponseInterceptor();

        return () => {
            api.interceptors.request.eject(requestInterceptor);
            api.interceptors.response.eject(responseInterceptor);
        };
    }, []);

    const value = useMemo(() => ({ api, setApiHeader }), []);

    return (
        <ApiContext.Provider value={value}>
            {showAuthenticationErrorModal && <ApplicationErrorModal type="authentication" />}

            {children}
        </ApiContext.Provider>
    );
}

export const useApiContext = () => useContext(ApiContext);
